import { createCodedError } from '~/common/errors/CodedError';
import {
  EsignatureCCRecipient,
  EsignatureRecipient,
  EsignatureSignerStatus,
} from '~/features/ticket/types/esignature';
import { JsonApiErrorResponse } from '~/redux/api/ApiTypes';
import { testIsAxiosError } from '~/redux/api/utils';
import { everest } from '~/services/serviceInstances';
import { PilotId } from '~/types/PilotId';
import { Uuid } from '~/types/Uuid';

type Request = {
  ticketId: Uuid;
};

type ServerSigner = {
  name: string | null;
  email: string | null;
  routing_order: number;
  recipient_id: string;
  pilot_user_id: PilotId | null;
  status: EsignatureSignerStatus;
  is_external: boolean;
};

type ServerCC = {
  name: string;
  email: string;
  routing_order: number;
  recipient_id: string;
};

type ServerResponse = {
  data: {
    attributes: {
      recipients: {
        signers: ServerSigner[];
        carbon_copies: ServerCC[];
      };
    };
  };
};

type Response = {
  signers: EsignatureRecipient[];
  carbonCopies: EsignatureCCRecipient[];
};

export const getRecipients = async (request: Request): Promise<Response> => {
  try {
    const response: ServerResponse = await everest.get(
      `tickets/${request.ticketId}/packages/recipients`,
    );

    return mapResponse(response);
  } catch (error) {
    if (testIsAxiosError<JsonApiErrorResponse>(error) && error.response) {
      const errorResponse = error.response.data.errors[0];
      throw createCodedError({
        message: errorResponse.detail ?? errorResponse.title,
        statusCode: errorResponse.status,
        title: errorResponse.title,
      });
    }

    throw error;
  }
};

const mapResponse = (serverResponse: ServerResponse): Response => {
  if (serverResponse.data === null) {
    return { signers: [], carbonCopies: [] };
  }

  return {
    signers:
      serverResponse.data.attributes.recipients.signers?.map((recipient) => ({
        isExternal: recipient.is_external,
        id: recipient.recipient_id,
        userId: recipient.pilot_user_id,
        status: recipient.status,
        external: recipient.email
          ? {
              name: recipient.name ?? '',
              email: recipient.email ?? '',
            }
          : null,
      })) ?? [],
    carbonCopies:
      serverResponse.data.attributes.recipients.carbon_copies?.map(
        (recipient) => ({
          id: recipient.recipient_id,
          name: recipient.name,
          email: recipient.email,
        }),
      ) ?? [],
  };
};
